import { useEffect, useState, type FormEvent } from "react";
import { crmMates, exportFichePack, forgetFiche, lookupSirene, mergeFiche, openFiche, saveFiche, type CrmMate, type SireneMatch } from "./api";
import { useAccess } from "./access";
import { coffreFileUrl, uploadCoffreFile } from "./coffre";
import { createFicheHome, isFicheHome, relocatePath, renameFicheHome } from "./crmHome";
import { ficheDoubles } from "./ficheIdentity";
import type { CrmFiche, CrmField, CrmSite } from "./types";

export function FicheDesk({
  fiche,
  others = [],
  onChange,
  onSaved,
  onOpen,
  onRetired,
}: {
  fiche: CrmFiche;
  others?: CrmFiche[];
  onChange: (fiche: CrmFiche) => void;
  onSaved: () => void;
  onOpen?: (fiche: CrmFiche) => void;
  onRetired?: (fiche: CrmFiche, close?: boolean) => void;
}) {
  const { canWrite, isAdmin } = useAccess();
  const [draft, setDraft] = useState(fiche);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState("");
  const [mates, setMates] = useState<CrmMate[]>(() => crmMates());
  const [hits, setHits] = useState<SireneMatch[]>([]);
  const [looking, setLooking] = useState(false);
  const [importing, setImporting] = useState("");
  const [forgetAsk, setForgetAsk] = useState(false);
  const doubles = ficheDoubles(draft, others);

  useEffect(() => {
    setDraft(fiche);
    setMates(crmMates());
  }, [fiche]);

  useEffect(() => {
    if (!draft.logo_path) {
      setLogoUrl("");
      return;
    }
    let alive = true;
    let objectUrl = "";
    void fetch(coffreFileUrl(draft.logo_path, fiche.coffre_owner_id || 0), { credentials: "include" })
      .then((res) => (res.ok ? res.blob() : null))
      .then((blob) => {
        if (!alive || !blob) return;
        objectUrl = URL.createObjectURL(blob);
        setLogoUrl(objectUrl);
      })
      .catch(() => {
        if (alive) setLogoUrl("");
      });
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [draft.logo_path]);

  async function onSave(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      let next = draft;
      if (next.kind === "particulier") next = { ...next, siren: "", siret: "" };
      else {
        const company = tidyCompany(next.siret || next.siren);
        if (company.error) {
          setError(company.error);
          setSaving(false);
          return;
        }
        next = { ...next, siret: company.siret, siren: company.siren };
      }
      const name = next.name.trim();
      if (name && name !== fiche.name && isFicheHome(fiche.coffre_path)) {
        const home = await renameFicheHome(fiche.coffre_path, name);
        next = { ...next, name, coffre_path: home, logo_path: relocatePath(next.logo_path, fiche.coffre_path, home) };
      } else {
        next = { ...next, name };
      }
      onChange(await saveFiche(next));
      onSaved();
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  }

  async function onLogo(file: File) {
    setError("");
    if (!fiche.coffre_path) {
      setError("Le dossier de la fiche dans CRM clients n’est pas prêt.");
      return;
    }
    try {
      const logoPath = await uploadCoffreFile(fiche.coffre_path, logoFile(file), fiche.coffre_owner_id || 0, true);
      onChange(await saveFiche({ ...draft, logo_path: logoPath }));
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Logo non envoyé");
    }
  }

  async function fuse(other: CrmFiche) {
    if (!canWrite) return;
    setSaving(true);
    setError("");
    try {
      const next = await mergeFiche(draft.id, other.id);
      onRetired?.(other);
      onChange(next);
      onSaved();
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Fusion impossible");
    } finally {
      setSaving(false);
    }
  }

  async function downloadPack() {
    setError("");
    try {
      const pack = await exportFichePack(draft.id);
      saveBlob(`${safeFile(draft.name)}.json`, JSON.stringify(pack, null, 2), "application/json");
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Export impossible");
    }
  }

  async function bringCsv(file: File) {
    if (!canWrite) return;
    setImporting("Lecture du fichier…");
    setError("");
    try {
      const rows = parseCsv(await file.text());
      let made = 0;
      let skipped = 0;
      for (const row of rows.slice(0, 40)) {
        const name = row.name.trim();
        if (!name) continue;
        const known = [draft, ...others].some((item) => item.name.trim().toLowerCase() === name.toLowerCase() || (row.email !== "" && item.email.trim().toLowerCase() === row.email.toLowerCase()));
        if (known) { skipped += 1; continue; }
        setImporting(name);
        const home = await createFicheHome(name);
        const created = await openFiche(home, 0, name);
        const saved = await saveFiche({ ...created, email: row.email, phone: row.phone, address: row.address, postal_code: row.postal_code, city: row.city, origin: row.origin });
        onChange(saved);
        made += 1;
      }
      setImporting(made === 0 ? "Aucune fiche nouvelle" : `${made} créée${made > 1 ? "s" : ""}${skipped > 0 ? ` · ${skipped} déjà là` : ""}`);
    } catch (reason: unknown) {
      setImporting("");
      setError(reason instanceof Error ? reason.message : "Import impossible");
    }
  }

  async function wipe() {
    if (!isAdmin) return;
    if (!forgetAsk) { setForgetAsk(true); return; }
    setSaving(true);
    setError("");
    try {
      await forgetFiche(draft.id);
      const gone = { ...draft, name: "Contact oublié", email: "", phone: "", address: "", postal_code: "", city: "", siren: "", siret: "", origin: "", tags: [], sites: [], fields: [], consent: false, legal_basis: "", consent_on: "", logo_path: "" };
      onRetired?.(gone, true);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Oubli impossible");
    } finally {
      setSaving(false);
      setForgetAsk(false);
    }
  }

  return (
    <div className="flex h-full items-center justify-center overflow-auto px-6 py-10">
      <form onSubmit={onSave} className="crm-rise crm-pop w-full max-w-lg rounded-3xl border border-slate-200 p-8 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
        <p className="text-center text-[11px] font-semibold uppercase tracking-[0.16em] crm-ink">Fiche</p>
        <h2 className="crm-title mt-1 text-center text-2xl font-semibold">Renseignements</h2>
        {error ? <p className="mt-4 text-center text-sm text-rose-700">{error}</p> : null}
        <div className="mt-6 flex items-center justify-center gap-4">
          <span className="grid h-16 w-16 place-items-center overflow-hidden rounded-2xl crm-soft text-xs font-semibold crm-ink">
            {logoUrl ? <img src={logoUrl} alt="" className="h-full w-full object-cover" /> : "Logo"}
          </span>
          <label className="cursor-pointer text-sm font-semibold crm-ink">
            Ajouter un logo
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void onLogo(file);
              }}
            />
          </label>
        </div>
        <div className="mt-6 grid gap-3">
          <Field label="Nom" value={draft.name} onChange={(name) => setDraft({ ...draft, name })} required />
          <div className="grid grid-cols-2 gap-2">
            <button type="button" className={draft.kind === "societe" ? "rounded-full crm-fill px-3 py-2 text-sm font-semibold text-white" : "crm-pill rounded-full px-3 py-2 text-sm font-semibold"} onClick={() => setDraft({ ...draft, kind: "societe" })}>Société</button>
            <button type="button" className={draft.kind === "particulier" ? "rounded-full crm-fill px-3 py-2 text-sm font-semibold text-white" : "crm-pill rounded-full px-3 py-2 text-sm font-semibold"} onClick={() => { setHits([]); setDraft({ ...draft, kind: "particulier", siren: "", siret: "" }); }}>Particulier</button>
          </div>
          {draft.kind === "societe" ? (
            <>
              <SireneField draft={draft} hits={hits} looking={looking} onDraft={setDraft} onHits={setHits} onLooking={setLooking} />
              <Field label="SIRET" value={draft.siret || ""} inputMode="numeric" onChange={(siret) => {
                const digits = siret.replace(/\D/g, "").slice(0, 14);
                setDraft({ ...draft, siret: digits, siren: digits.length >= 9 ? digits.slice(0, 9) : "" });
              }} />
            </>
          ) : null}
          <Field label="Origine" value={draft.origin || ""} onChange={(origin) => setDraft({ ...draft, origin })} placeholder="Bouche-à-oreille, site, salon, appel, réseau…" />
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium text-slate-500">Responsable</span>
            <select className="crm-field w-full rounded-xl border px-3 py-2" value={draft.owner_user_id || ""} onChange={(event) => {
              const owner_user_id = Number(event.target.value) || 0;
              const owner_name = mates.find((mate) => mate.id === owner_user_id)?.name || "";
              setDraft({ ...draft, owner_user_id, owner_name });
            }}>
              <option value="">Non attribué</option>
              {draft.owner_user_id > 0 && !mates.some((mate) => mate.id === draft.owner_user_id) ? <option value={draft.owner_user_id}>{draft.owner_name || "Responsable"}</option> : null}
              {mates.map((mate) => <option key={mate.id} value={mate.id}>{mate.name}</option>)}
            </select>
          </label>
          <TagsField tags={draft.tags || []} onChange={(tags) => setDraft({ ...draft, tags })} />
          {doubles.length > 0 ? (
            <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950">
              <p className="font-semibold">Déjà une fiche</p>
              <ul className="mt-1 space-y-2">
                {doubles.map((hit) => (
                  <li key={hit.fiche.id} className="flex flex-wrap items-center gap-2">
                    <span>
                      {onOpen ? <button type="button" className="font-semibold underline" onClick={() => onOpen(hit.fiche)}>{hit.fiche.name}</button> : <span className="font-semibold">{hit.fiche.name}</span>}
                      <span> — {hit.why}</span>
                    </span>
                    {canWrite ? <button type="button" className="rounded-full border border-amber-400 px-2 py-0.5 text-xs font-semibold" onClick={() => void fuse(hit.fiche)}>Fusionner ici</button> : null}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs">La fusion garde cette fiche. L’autre est archivée. Son dossier Coffre reste à part.</p>
            </div>
          ) : null}
          <Field label="E-mail" value={draft.email} onChange={(email) => setDraft({ ...draft, email })} type="email" />
          <Field label="Téléphone" value={draft.phone} onChange={(phone) => setDraft({ ...draft, phone })} />
          <Field label="Adresse" value={draft.address} onChange={(address) => setDraft({ ...draft, address })} />
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Code postal" value={draft.postal_code} onChange={(postal_code) => setDraft({ ...draft, postal_code })} />
            <Field label="Ville" value={draft.city} onChange={(city) => setDraft({ ...draft, city })} />
          </div>
          <SitesField sites={draft.sites || []} onChange={(sites) => setDraft({ ...draft, sites })} />
          <FieldsField fields={draft.fields || []} onChange={(fields) => setDraft({ ...draft, fields })} />
          <PrivacyField
            draft={draft}
            onDraft={setDraft}
            canWrite={canWrite}
            isAdmin={isAdmin}
            importing={importing}
            forgetAsk={forgetAsk}
            onForgetAsk={setForgetAsk}
            onExport={() => void downloadPack()}
            onCsv={() => downloadCsv([draft, ...others.filter((row) => row.id !== draft.id)])}
            onImport={(file) => void bringCsv(file)}
            onForget={() => void wipe()}
          />
        </div>
        <button disabled={saving} className="mt-6 w-full rounded-full crm-fill px-5 py-2.5 text-sm font-semibold text-white">
          {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
      </form>
    </div>
  );
}

function logoFile(file: File): File {
  const raw = file.name.split(".").pop()?.toLowerCase() || "";
  const known = raw === "jpeg" ? "jpg" : raw;
  const ext = /^(png|jpg|gif|webp|svg|bmp|avif)$/.test(known)
    ? known
    : file.type === "image/png" ? "png"
    : file.type === "image/webp" ? "webp"
    : file.type === "image/gif" ? "gif"
    : "jpg";
  return new File([file], `logo-crm.${ext}`, { type: file.type || "image/jpeg" });
}

function tidyCompany(raw: string): { siret: string; siren: string; error: string } {
  const digits = raw.replace(/\D/g, "");
  if (digits === "") return { siret: "", siren: "", error: "" };
  if (digits.length === 14) return { siret: digits, siren: digits.slice(0, 9), error: "" };
  if (digits.length === 9) return { siret: "", siren: digits, error: "" };
  return { siret: raw, siren: "", error: "Le SIRET fait 14 chiffres." };
}

function applySirene(draft: CrmFiche, match: SireneMatch): CrmFiche {
  return {
    ...draft,
    kind: "societe",
    name: match.name || draft.name,
    siret: match.siret,
    siren: match.siren,
    address: match.address || draft.address,
    postal_code: match.postal_code || draft.postal_code,
    city: match.city || draft.city,
  };
}

function sireneNote(status: SireneMatch["status"]): string {
  if (status === "ceased") return "Société cessée";
  if (status === "closed") return "Établissement fermé";
  return "Active";
}

function SireneField({
  draft,
  hits,
  looking,
  onDraft,
  onHits,
  onLooking,
}: {
  draft: CrmFiche;
  hits: SireneMatch[];
  looking: boolean;
  onDraft: (fiche: CrmFiche) => void;
  onHits: (hits: SireneMatch[]) => void;
  onLooking: (busy: boolean) => void;
}) {
  const [query, setQuery] = useState("");
  const [fault, setFault] = useState("");
  const [taken, setTaken] = useState("");

  function take(match: SireneMatch) {
    onDraft(applySirene(draft, match));
    onHits([]);
    setQuery("");
    setFault("");
    setTaken(`${match.name || "Établissement"} — ${sireneNote(match.status)}`);
  }

  async function search(raw: string) {
    const text = raw.trim();
    if (text === "") {
      setFault("Indique un SIRET ou une raison sociale.");
      setTaken("");
      return;
    }
    onLooking(true);
    setFault("");
    setTaken("");
    onHits([]);
    try {
      const matches = await lookupSirene(text);
      if (matches.length === 1) {
        take(matches[0]);
        return;
      }
      onHits(matches);
    } catch (reason: unknown) {
      setFault(reason instanceof Error ? reason.message : "La recherche INSEE n’a pas abouti.");
    } finally {
      onLooking(false);
    }
  }

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200">
      <div className="crm-head border-b px-3 py-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em]">Recherche INSEE</p>
        <p className="mt-0.5 text-xs font-normal normal-case tracking-normal">Annuaire des entreprises. Le nom, le SIRET et l’adresse se remplissent.</p>
      </div>
      <div className="grid gap-2 p-3">
        <div className="flex gap-2">
          <input
            value={query}
            placeholder="SIRET ou raison sociale"
            aria-label="Recherche INSEE"
            className="crm-field min-w-0 flex-1 rounded-xl border px-3 py-2 text-sm"
            onChange={(event) => { setQuery(event.target.value); setFault(""); }}
            onKeyDown={(event) => {
              if (event.key !== "Enter") return;
              event.preventDefault();
              void search(event.currentTarget.value);
            }}
          />
          <button type="button" className="crm-pill shrink-0 rounded-full px-3 text-xs font-semibold" disabled={looking} onClick={() => void search(query)}>
            {looking ? "Recherche…" : "Rechercher"}
          </button>
        </div>
        {fault ? <p className="text-xs text-rose-700">{fault}</p> : null}
        {taken ? <p className="text-xs font-medium text-slate-600">{taken}</p> : null}
        {hits.length > 0 ? (
          <ul className="overflow-hidden rounded-xl border border-slate-200">
            {hits.map((match) => (
              <li key={match.siret} className="border-b border-slate-200 last:border-b-0">
                <button type="button" className="flex w-full flex-col px-3 py-2 text-left hover:bg-black/5" onClick={() => take(match)}>
                  <span className="text-sm font-semibold">{match.name}</span>
                  <span className="text-xs text-slate-500">{match.siret} · {[match.postal_code, match.city].filter(Boolean).join(" ")} · {sireneNote(match.status)}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}

const BASES = [
  { id: "", label: "Non précisée" },
  { id: "contrat", label: "Contrat" },
  { id: "interet", label: "Intérêt légitime" },
  { id: "obligation", label: "Obligation légale" },
  { id: "consentement", label: "Consentement" },
];

function SitesField({ sites, onChange }: { sites: CrmSite[]; onChange: (sites: CrmSite[]) => void }) {
  function patch(index: number, next: Partial<CrmSite>) {
    onChange(sites.map((site, at) => at === index ? { ...site, ...next } : site));
  }
  return (
    <section className="rounded-2xl border border-slate-200 p-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Sites</p>
      <div className="mt-2 grid gap-3">
        {sites.map((site, index) => (
          <div key={index} className="grid gap-2">
            <Field label="Nom du site" value={site.label} onChange={(label) => patch(index, { label })} placeholder="Siège, chantier, agence…" />
            <Field label="Adresse" value={site.address} onChange={(address) => patch(index, { address })} />
            <div className="grid gap-2 sm:grid-cols-2">
              <Field label="Code postal" value={site.postal_code} onChange={(postal_code) => patch(index, { postal_code })} />
              <Field label="Ville" value={site.city} onChange={(city) => patch(index, { city })} />
            </div>
            <button type="button" className="justify-self-start text-xs font-semibold text-rose-700" onClick={() => onChange(sites.filter((_, at) => at !== index))}>Retirer ce site</button>
          </div>
        ))}
      </div>
      {sites.length < 12 ? <button type="button" className="mt-2 text-sm font-semibold crm-ink" onClick={() => onChange([...sites, { label: "", address: "", postal_code: "", city: "" }])}>+ Site</button> : null}
    </section>
  );
}

function FieldsField({ fields, onChange }: { fields: CrmField[]; onChange: (fields: CrmField[]) => void }) {
  function patch(index: number, next: Partial<CrmField>) {
    onChange(fields.map((field, at) => at === index ? { ...field, ...next } : field));
  }
  return (
    <section className="rounded-2xl border border-slate-200 p-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Champs libres</p>
      <div className="mt-2 grid gap-2">
        {fields.map((field, index) => (
          <div key={index} className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto] sm:items-end">
            <Field label="Intitulé" value={field.label} onChange={(label) => patch(index, { label })} />
            <Field label="Valeur" value={field.value} onChange={(value) => patch(index, { value })} />
            <button type="button" className="mb-2 text-xs font-semibold text-rose-700" onClick={() => onChange(fields.filter((_, at) => at !== index))}>Retirer</button>
          </div>
        ))}
      </div>
      {fields.length < 16 ? <button type="button" className="mt-2 text-sm font-semibold crm-ink" onClick={() => onChange([...fields, { label: "", value: "" }])}>+ Champ</button> : null}
    </section>
  );
}

function PrivacyField({
  draft, onDraft, canWrite, isAdmin, importing, forgetAsk, onForgetAsk, onExport, onCsv, onImport, onForget,
}: {
  draft: CrmFiche;
  onDraft: (fiche: CrmFiche) => void;
  canWrite: boolean;
  isAdmin: boolean;
  importing: string;
  forgetAsk: boolean;
  onForgetAsk: (value: boolean) => void;
  onExport: () => void;
  onCsv: () => void;
  onImport: (file: File) => void;
  onForget: () => void;
}) {
  return (
    <section className="rounded-2xl border border-slate-200 p-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">RGPD</p>
      <label className="mt-2 flex items-center gap-2 text-sm">
        <input type="checkbox" checked={draft.consent === true} onChange={(event) => onDraft({ ...draft, consent: event.target.checked })} />
        Consentement recueilli
      </label>
      <label className="mt-2 block text-sm">
        <span className="mb-1 block text-xs font-medium text-slate-500">Base légale</span>
        <select className="crm-field w-full rounded-xl border px-3 py-2" value={draft.legal_basis || ""} onChange={(event) => onDraft({ ...draft, legal_basis: event.target.value })}>
          {BASES.map((item) => <option key={item.id || "none"} value={item.id}>{item.label}</option>)}
        </select>
      </label>
      <Field label="Date" value={draft.consent_on || ""} type="date" onChange={(consent_on) => onDraft({ ...draft, consent_on })} />
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold" onClick={onExport}>Exporter la fiche</button>
        <button type="button" className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold" onClick={onCsv}>Export CSV</button>
        {canWrite ? (
          <label className="cursor-pointer rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold">
            {importing || "Import CSV"}
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ""; if (file) onImport(file); }} />
          </label>
        ) : null}
        {isAdmin ? <button type="button" className="rounded-full px-3 py-1.5 text-xs font-semibold text-rose-700" onClick={onForget}>{forgetAsk ? "Confirmer l’oubli" : "Oubli"}</button> : null}
        {forgetAsk ? <button type="button" className="text-xs font-semibold text-slate-500" onClick={() => onForgetAsk(false)}>Annuler</button> : null}
      </div>
      <p className="mt-2 text-xs text-slate-500">L’oubli retire les données personnelles et archive la fiche. Le dossier dans CRM clients reste à vider s’il contient encore des notes.</p>
    </section>
  );
}

function saveBlob(name: string, body: string, type: string) {
  const blob = new Blob([body], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

function downloadCsv(rows: CrmFiche[]) {
  const header = ["nom", "code", "email", "telephone", "adresse", "code_postal", "ville", "origine", "siret", "consentement", "base_legale", "date_consentement"];
  const lines = [header.join(";")];
  rows.forEach((row) => {
    lines.push([row.name, row.code, row.email, row.phone, row.address, row.postal_code, row.city, row.origin, row.siret, row.consent ? "oui" : "non", row.legal_basis, row.consent_on].map(csvCell).join(";"));
  });
  saveBlob("fiches-crm.csv", lines.join("\n"), "text/csv;charset=utf-8");
}

function csvCell(value: string): string {
  const text = String(value || "");
  return /[;"\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function parseCsv(raw: string): Array<{ name: string; email: string; phone: string; address: string; postal_code: string; city: string; origin: string }> {
  const lines = raw.replace(/^\uFEFF/, "").split(/\r?\n/).filter((line) => line.trim() !== "");
  if (lines.length < 2) return [];
  const head = splitCsv(lines[0]).map((cell) => foldHead(cell));
  const at = (names: string[]) => head.findIndex((cell) => names.includes(cell));
  const nameAt = at(["nom", "name", "raison", "societe", "société"]);
  const mailAt = at(["email", "e-mail", "mail", "courriel"]);
  const phoneAt = at(["telephone", "téléphone", "tel", "phone"]);
  const addressAt = at(["adresse", "address"]);
  const postalAt = at(["code_postal", "code postal", "cp", "postal"]);
  const cityAt = at(["ville", "city"]);
  const originAt = at(["origine", "source"]);
  return lines.slice(1).map((line) => {
    const cells = splitCsv(line);
    const pick = (index: number) => index >= 0 ? (cells[index] || "").trim() : "";
    return { name: pick(nameAt), email: pick(mailAt), phone: pick(phoneAt), address: pick(addressAt), postal_code: pick(postalAt), city: pick(cityAt), origin: pick(originAt) };
  }).filter((row) => row.name !== "");
}

function splitCsv(line: string): string[] {
  const out: string[] = [];
  let cell = "";
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === '"') {
      if (quoted && line[index + 1] === '"') { cell += '"'; index += 1; }
      else quoted = !quoted;
    } else if ((char === ";" || char === ",") && !quoted) {
      out.push(cell);
      cell = "";
    } else cell += char;
  }
  out.push(cell);
  return out;
}

function foldHead(value: string): string {
  return value.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().trim();
}

function safeFile(name: string): string {
  return name.trim().replace(/[^\p{L}\p{N}._-]+/gu, "-").replace(/^-+|-+$/g, "") || "fiche";
}

function TagsField({ tags, onChange }: { tags: string[]; onChange: (tags: string[]) => void }) {
  const [text, setText] = useState("");

  function commit(raw: string) {
    const next = [...tags];
    const seen = new Set(tags.map((tag) => tag.toLocaleLowerCase("fr")));
    for (const part of raw.split(/[,;]/)) {
      const tag = part.trim().replace(/\s+/g, " ").slice(0, 32);
      const key = tag.toLocaleLowerCase("fr");
      if (tag === "" || seen.has(key) || next.length >= 24) continue;
      seen.add(key);
      next.push(tag);
    }
    onChange(next);
    setText("");
  }

  return (
    <label className="block text-sm">
      <span className="mb-1 block text-xs font-medium text-slate-500">Tags</span>
      <span className="crm-field flex min-h-10 flex-wrap items-center gap-1 rounded-xl border px-2 py-1.5">
        {tags.map((tag) => (
          <button key={tag} type="button" className="crm-soft rounded-full px-2 py-0.5 text-xs font-semibold" onClick={() => onChange(tags.filter((item) => item !== tag))}>
            {tag} ×
          </button>
        ))}
        <input
          value={text}
          placeholder={tags.length === 0 ? "Entrée ou virgule" : ""}
          className="min-w-28 flex-1 bg-transparent px-1 py-1 text-sm outline-none"
          onChange={(event) => setText(event.target.value)}
          onBlur={() => { if (text.trim() !== "") commit(text); }}
          onKeyDown={(event) => {
            if (event.key !== "Enter" && event.key !== ",") return;
            event.preventDefault();
            commit(text);
          }}
        />
      </span>
    </label>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required = false,
  inputMode,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
  inputMode?: "numeric" | "text";
  placeholder?: string;
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-xs font-medium text-slate-500">{label}</span>
      <input
        required={required}
        type={type}
        inputMode={inputMode}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="crm-field w-full rounded-xl border px-3 py-2"
      />
    </label>
  );
}
